import { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';

const formatError = (detail) => {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (user) {
    const dest = location.state?.from?.pathname || '/';
    return <Navigate to={dest} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      const dest = location.state?.from?.pathname || '/';
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center px-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-10">
          <img src="/logo.png" alt="Ralicense.in" className="h-16 w-auto object-contain" />
        </div>

        <div className="bg-white rounded-3xl border border-mute/30 shadow-card p-10">
          <h1 className="font-display text-3xl font-bold text-ink tracking-tight">Sign in</h1>
          <p className="text-ink/60 mt-1 mb-8">Welcome to RA License by Abroad Papers</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="text-sm font-medium text-ink/70 mb-2 block">Email</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@ralicense.in"
                autoFocus
                required
                className="w-full bg-transparent border-b-2 border-ink/15 focus:border-primary focus:outline-none pb-2 text-lg font-medium text-ink placeholder:text-ink/30 transition-colors"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-ink/70 mb-2 block">Password</label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="w-full bg-transparent border-b-2 border-ink/15 focus:border-primary focus:outline-none pb-2 text-lg font-medium text-ink placeholder:text-ink/30 transition-colors"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit"
              type="submit"
              disabled={submitting}
              className="w-full h-12 rounded-full bg-primary text-white font-semibold shadow-lg shadow-primary/20 hover:bg-primary-hover transition-all disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-5 h-5 animate-spin" />}
              Sign in
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-ink/40 mt-6">
          © 2026 Ralicense.in — Internal access only
        </p>
      </div>
    </div>
  );
};

export default Login;
